import { buildDescription } from './antifraudAnalysisMapper'
import {
  IAntifraudApiChange,
  IAntifraudApiEvent,
  IAntifraudSavedReview,
  _resendableSolution
} from './antifraudAnalysisTypes'

export const API_SOLUTION_LABEL: { [solution in _resendableSolution]: string } = {
  unico: 'Único',
  emailAge: 'EmailAge',
  acertPix: 'AcertPix'
}

export function apiEventTitle (event: IAntifraudApiEvent): string {
  const label = API_SOLUTION_LABEL[event.solution] ?? event.solution

  switch (event.kind) {
    case 'requery': return `Reconsulta do ${label}`
    case 'resend': return `Reenvio do ${label}`
    default: return event.solution === 'acertPix' ? 'Abertura do AcertPix' : `Disparo do ${label}`
  }
}

// Uma linha por validação que a API devolveu, como na tabela de processos
export function apiEventResultLines (event: IAntifraudApiEvent): string[] {
  if (event.error) {
    const attempts = event.attempts ? ` (após ${event.attempts} tentativas)` : ''

    return [`Erro: ${event.error}${attempts}`]
  }

  if (!event.result?.length) return ['Aguardando retorno']

  return event.result.map(row => `${row.type}: ${buildDescription(row)}`)
}

export function changeText (change: IAntifraudApiChange, separator = ' → '): string {
  return `${change.before || '-'}${separator}${change.after || '-'}`
}

export interface IAntifraudApiUpdate {
  event: IAntifraudApiEvent
  previous?: IAntifraudApiEvent
}

// Atualizações depois do primeiro antifraude: reconsultas por dado alterado e reenvios manuais, cada
// um com a consulta anterior da mesma API para comparar o resultado. O line-server guarda o resultado
// que valia na hora da chamada; sem ele (chamadas antigas), vale a consulta anterior registrada
export function participantUpdates (events: IAntifraudApiEvent[] = []): IAntifraudApiUpdate[] {
  const ordered = [...events].sort((first, second) => first.createdAt.localeCompare(second.createdAt))

  const recorded = (event: IAntifraudApiEvent): IAntifraudApiEvent | undefined => (
    event.previousResult?.length
      ? { ...event, error: undefined, attempts: undefined, result: event.previousResult }
      : undefined
  )

  return ordered
    .map((event, index) => ({ event, index }))
    .filter(({ event }) => event.kind !== 'dispatch')
    .map(({ event, index }) => ({
      event,
      previous: recorded(event) ?? ordered
        .slice(0, index)
        .reverse()
        .find(candidate => candidate.solution === event.solution && candidate.result?.length)
    }))
}

export type _historyEntry =
  | { type: 'review', at: string, review: IAntifraudSavedReview }
  | { type: 'api', at: string, event: IAntifraudApiEvent }

// Pareceres e chamadas de API numa linha só, da mais recente para a mais antiga
export function historyTimeline (
  reviews: IAntifraudSavedReview[] = [],
  events: IAntifraudApiEvent[] = []
): _historyEntry[] {
  const entries: _historyEntry[] = [
    ...reviews.map(review => ({ type: 'review' as const, at: review.createdAt, review })),
    ...events.map(event => ({ type: 'api' as const, at: event.createdAt, event }))
  ]

  return entries.sort((first, second) => second.at.localeCompare(first.at))
}
